/**
 * useAdminColors
 *
 * Admin-flavoured wrapper over `useThemeColors()`. Exposes the same theme data
 * with admin-component tokens hoisted to a top-level `admin` key for ergonomics:
 *
 *   const { admin, redesign, semantic, mode } = useAdminColors();
 *   <View style={{ backgroundColor: admin.dataTable.bg }} />
 *
 * Provides a single seam for evolving admin-specific theming concerns.
 */
import { useMemo } from 'react';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import type {
  ThemeMode,
  RedesignThemeColors,
  SemanticThemeColors,
  AdminComponentTheme,
} from '@/constants/shared/theme';

export interface AdminColorsValue {
  mode: ThemeMode;
  redesign: RedesignThemeColors;
  semantic: SemanticThemeColors;
  admin: AdminComponentTheme;
}

export function useAdminColors(): AdminColorsValue {
  const { mode, colors, semantic, components } = useThemeColors();
  return useMemo(
    () => ({
      mode,
      redesign: colors,
      semantic,
      admin: components.admin,
    }),
    [mode, colors, semantic, components.admin],
  );
}
